import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown, ChevronRight, Target, Cog, Users, GraduationCap, UserPlus, Rocket, Compass, ClipboardCheck, Map, Building2, Wrench, BookOpen, Briefcase, BarChart3, UserCheck, Award, Trophy, Search, CheckCircle, UserCog, Phone, TrendingUp, Zap, Globe } from 'lucide-react';
import Logo from './Logo';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMegaMenu, setOpenMegaMenu] = useState(null);
  const [activeCategory, setActiveCategory] = useState('strategie');
  const navRef = useRef(null);

  // Fermer le menu quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenMegaMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Changer la langue
  const toggleLanguage = () => {
    const newLang = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
  };

  // Catégories de services avec leurs sous-items
  const serviceCategories = [
    {
      id: 'strategie',
      icon: Target,
      titleKey: 'serviceCategories.strategie.title',
      descKey: 'serviceCategories.strategie.description',
      href: '/services/sparring',
      items: [
        { icon: Compass, titleKey: 'serviceItems.sparringPartner.title', descKey: 'serviceItems.sparringPartner.desc', href: '/services/sparring#sparring-partner' },
        { icon: ClipboardCheck, titleKey: 'serviceItems.auditCommercial.title', descKey: 'serviceItems.auditCommercial.desc', href: '/services/sparring#audit' },
        { icon: Map, titleKey: 'serviceItems.businessPlan.title', descKey: 'serviceItems.businessPlan.desc', href: '/services/sparring#business-plan' },
      ]
    },
    {
      id: 'ingenierie',
      icon: Cog,
      titleKey: 'serviceCategories.ingenierie.title',
      descKey: 'serviceCategories.ingenierie.description',
      href: '/services/architecte',
      items: [
        { icon: Building2, titleKey: 'serviceItems.architectureCommerciale.title', descKey: 'serviceItems.architectureCommerciale.desc', href: '/services/architecte#architecture' },
        { icon: Wrench, titleKey: 'serviceItems.stackOutils.title', descKey: 'serviceItems.stackOutils.desc', href: '/services/architecte#stack-outils' },
        { icon: BookOpen, titleKey: 'serviceItems.playbooks.title', descKey: 'serviceItems.playbooks.desc', href: '/services/architecte#playbooks' },
      ]
    },
    {
      id: 'execution',
      icon: Users,
      titleKey: 'serviceCategories.execution.title',
      descKey: 'serviceCategories.execution.description',
      href: '/services/pilote',
      items: [
        { icon: Briefcase, titleKey: 'serviceItems.directionCommerciale.title', descKey: 'serviceItems.directionCommerciale.desc', href: '/services/pilote#direction' },
        { icon: BarChart3, titleKey: 'serviceItems.pilotageKpis.title', descKey: 'serviceItems.pilotageKpis.desc', href: '/services/pilote#kpis' },
        { icon: UserCheck, titleKey: 'serviceItems.accompagnementTerrain.title', descKey: 'serviceItems.accompagnementTerrain.desc', href: '/services/pilote#accompagnement' },
      ]
    },
    {
      id: 'formation',
      icon: GraduationCap,
      titleKey: 'serviceCategories.formation.title',
      descKey: 'serviceCategories.formation.description',
      href: '/services/coach',
      items: [
        { icon: Award, titleKey: 'serviceItems.coachingCommercial.title', descKey: 'serviceItems.coachingCommercial.desc', href: '/services/coach#coaching' },
        { icon: Trophy, titleKey: 'serviceItems.bootcampClosing.title', descKey: 'serviceItems.bootcampClosing.desc', href: '/services/coach#bootcamp' },
        { icon: Phone, titleKey: 'serviceItems.programmeProspection.title', descKey: 'serviceItems.programmeProspection.desc', href: '/services/coach#prospection' },
      ]
    },
    {
      id: 'recrutement',
      icon: UserPlus,
      titleKey: 'serviceCategories.recrutement.title',
      descKey: 'serviceCategories.recrutement.description',
      href: '/services/recruteur',
      items: [
        { icon: Search, titleKey: 'serviceItems.chasseProfils.title', descKey: 'serviceItems.chasseProfils.desc', href: '/services/recruteur#chasse' },
        { icon: CheckCircle, titleKey: 'serviceItems.assessmentCenter.title', descKey: 'serviceItems.assessmentCenter.desc', href: '/services/recruteur#assessment' },
        { icon: UserCog, titleKey: 'serviceItems.onboardingSales.title', descKey: 'serviceItems.onboardingSales.desc', href: '/services/recruteur#onboarding' },
      ]
    },
    {
      id: 'acceleration',
      icon: Rocket,
      titleKey: 'serviceCategories.acceleration.title',
      descKey: 'serviceCategories.acceleration.description',
      href: '/services/accelerateur',
      items: [
        { icon: Phone, titleKey: 'serviceItems.prospectionExternalisee.title', descKey: 'serviceItems.prospectionExternalisee.desc', href: '/services/accelerateur#prospection' },
        { icon: TrendingUp, titleKey: 'serviceItems.growthCommercial.title', descKey: 'serviceItems.growthCommercial.desc', href: '/services/accelerateur#growth' },
        { icon: Zap, titleKey: 'serviceItems.scaleupRevenue.title', descKey: 'serviceItems.scaleupRevenue.desc', href: '/services/accelerateur#scaleup' },
      ]
    },
  ];

  // Toggle le menu au clic
  const handleMenuClick = (menuName) => {
    setOpenMegaMenu(openMegaMenu === menuName ? null : menuName);
    if (menuName === 'services') {
      setActiveCategory('strategie');
    }
  };

  // Fermer le menu quand on clique sur un lien
  const handleLinkClick = () => {
    setOpenMegaMenu(null);
  };

  const activeCategoryData = serviceCategories.find(cat => cat.id === activeCategory);

  return (
    <div ref={navRef}>
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo FOMOW - version noire pour fond clair */}
            <Link to="/" className="flex items-center group hover:opacity-70 transition-opacity relative z-50 mr-8">
              <Logo variant="black" height={24} />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-2">
              {/* Nos Services et Expertises */}
              <button 
                onClick={() => handleMenuClick('services')}
                className={`px-3 py-2 text-[15px] text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-1.5 rounded-lg whitespace-nowrap ${openMegaMenu === 'services' ? 'bg-gray-100 text-gray-900' : ''}`}
              >
                {t('nav.services')}
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openMegaMenu === 'services' ? 'rotate-180' : ''}`} />
              </button>

              <Link to="/case-studies" onClick={handleLinkClick} className="px-3 py-2 text-[15px] text-gray-600 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">{t('nav.cases')}</Link>
              <Link to="/about" onClick={handleLinkClick} className="px-3 py-2 text-[15px] text-gray-600 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">{t('nav.about')}</Link>
            </div>

            {/* CTA Buttons - Prendre RDV + WhatsApp + Language Switcher */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Language Switcher */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Toggle language"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm font-medium uppercase">{i18n.language === 'fr' ? 'EN' : 'FR'}</span>
              </button>

              <a 
                href="https://meetings-eu1.hubspot.com/younes8" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 bg-[#2A41E5] text-white font-bold rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 hover:bg-[#1E32B8] whitespace-nowrap"
              >
                <span className="relative flex h-2 w-2 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                <span className="uppercase text-sm tracking-wide whitespace-nowrap">{t('nav.bookCall')}</span>
              </a>
              
              {/* WhatsApp */}
              <a
                href="https://wa.me/33745891319"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-10 h-10 bg-[#25D366] rounded-full shadow-md hover:shadow-lg hover:shadow-[#25D366]/30 transition-all duration-300 hover:scale-110 flex-shrink-0"
                aria-label={t('nav.whatsapp')}
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Toggle menu">
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mega Menu Services - Style Bulldozer */}
      <div 
        className={`fixed top-[72px] left-0 right-0 z-40 transition-all duration-300 ${openMegaMenu === 'services' ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-4 pointer-events-none'}`}
      >
        <div className="bg-white border-b border-gray-200 shadow-2xl">
          <div className="max-w-6xl mx-auto px-6 py-0">
            <div className="flex">
              {/* Colonne gauche - Catégories */}
              <div className="w-72 border-r border-gray-100 py-6 pr-4">
                {serviceCategories.map((category) => {
                  const IconComponent = category.icon;
                  const isActive = activeCategory === category.id;
                  return (
                    <Link
                      key={category.id}
                      to={category.href}
                      onClick={handleLinkClick}
                      onMouseEnter={() => setActiveCategory(category.id)}
                      className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                        isActive 
                          ? 'bg-[#041C2C] text-white' 
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <IconComponent className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-[#2A41E5]'}`} />
                      <div className="flex-1">
                        <div className={`font-semibold text-sm ${isActive ? 'text-white' : 'text-gray-900'}`}>
                          {t(category.titleKey)}
                        </div>
                        <div className={`text-xs ${isActive ? 'text-white/70' : 'text-gray-500'}`}>
                          {t(category.descKey)}
                        </div>
                      </div>
                      <ChevronRight className={`w-4 h-4 transition-opacity ${isActive ? 'opacity-100 text-white' : 'opacity-0'}`} />
                    </Link>
                  );
                })}
              </div>

              {/* Colonne droite - Sous-items de la catégorie active */}
              <div className="flex-1 py-6 pl-8">
                {activeCategoryData && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
                      <activeCategoryData.icon className="w-5 h-5 text-[#2A41E5]" />
                      <div>
                        <h3 className="font-bold text-gray-900">{t(activeCategoryData.titleKey)}</h3>
                        <p className="text-sm text-gray-500">{t(activeCategoryData.descKey)}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-1">
                      {activeCategoryData.items.map((item, idx) => {
                        const ItemIcon = item.icon;
                        return (
                          <Link
                            key={idx}
                            to={item.href}
                            onClick={handleLinkClick}
                            className="group flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-all duration-200"
                          >
                            <ItemIcon className="w-5 h-5 text-[#2A41E5] flex-shrink-0" />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 group-hover:text-[#2A41E5] transition-colors">
                                {t(item.titleKey)}
                              </div>
                              <div className="text-sm text-gray-500">{t(item.descKey)}</div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#2A41E5] group-hover:translate-x-1 transition-all" />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed top-[72px] left-0 right-0 bottom-0 z-40 lg:hidden bg-white overflow-y-auto">
          <div className="p-6 space-y-2">
            {/* Language Switcher Mobile */}
            <div className="flex justify-end mb-4">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Globe className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">{i18n.language === 'fr' ? 'English' : 'Français'}</span>
              </button>
            </div>

            {/* Services accordion mobile */}
            <div className="border-b border-gray-100 pb-4">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-3 font-medium">{t('nav.mobileServices')}</p>
              <div className="space-y-2">
                {serviceCategories.map((category) => {
                  const IconComponent = category.icon;
                  return (
                    <Link
                      key={category.id}
                      to={category.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      <IconComponent className="w-5 h-5 text-[#2A41E5]" />
                      <div>
                        <div className="font-semibold text-gray-900">{t(category.titleKey)}</div>
                        <div className="text-xs text-gray-500">{t(category.descKey)}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Other links */}
            <Link to="/case-studies" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 py-4 text-gray-700 hover:text-gray-900 font-medium border-b border-gray-100">
              <span>📊</span> {t('nav.cases')}
            </Link>
            <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 py-4 text-gray-700 hover:text-gray-900 font-medium border-b border-gray-100">
              <span>👥</span> {t('nav.about')}
            </Link>
            
            {/* CTAs */}
            <div className="pt-6 space-y-3">
              <a 
                href="https://meetings-eu1.hubspot.com/younes8" 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={() => setIsMobileMenuOpen(false)} 
                className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-[#2A41E5] text-white font-bold rounded-xl text-center hover:bg-[#1E32B8] transition-colors"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                {t('nav.bookCall')}
              </a>
              <a 
                href="https://wa.me/33745891319" 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={() => setIsMobileMenuOpen(false)} 
                className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-[#25D366] text-white font-bold rounded-xl text-center hover:bg-[#20BD5A] transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                {t('nav.whatsapp')}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
